import React, { Component } from 'react';
import { connect } from 'react-redux';
import * as Yup from 'yup';
import { FormattedMessage, injectIntl } from 'react-intl';
import PropTypes from 'prop-types';
import { Formik } from 'formik';
import { Panel } from 'primereact/panel';
import { withAccount } from '../../../components/AccountContext';
import { searchJobProfile } from '../../../services/apps/JobProfileService';
import { withGrowl } from '../../../components/GrowlNotifications';
import { growlErrors } from '../../../utils/NotificationUtil';
import { growlShape } from '../../../components/prop-types';
import NewAssessmentForm from './NewAssessmentForm';
import { searchAsmtTemp } from '../../../services/apps/AssessmentTempServices';
import { validationMessages } from '../../../i18n/common';

const buildValidationSchema = formatMessage => Yup.object().shape({
  assessmentTemplate: Yup.object().required(formatMessage(validationMessages.required)),
  emails: Yup.array().when('sendReportsWhenAssessmentIsComplete', {
    is: true,
    then: () => Yup.array().required(formatMessage(validationMessages.required)),
  }),
  autogenerateReports: Yup.array().when('sendReportsWhenAssessmentIsComplete', {
    is: true,
    then: () => Yup.array().required(formatMessage(validationMessages.required)),
  }),
  targetJob: Yup.object().when('sendReportsWhenAssessmentIsComplete', {
    is: true,
    then: () => Yup.object().required(formatMessage(validationMessages.required)),
  }),
  locale: Yup.string().when('sendReportsWhenAssessmentIsComplete', {
    is: true,
    then: () => Yup.string().required(formatMessage(validationMessages.required)),
  }),
});

class NewAssessment extends Component {
  constructor() {
    super();
    this.state = {
      jobs: [],
      asmtTemp: null,
      jobSearching: false,
      temps: [],
      isTempLoading: false,
      completedReportAction: false,
    };
    this.handleSelectTemplate = this.handleSelectTemplate.bind(this);
    this.handleSearchJobProfile = this.handleSearchJobProfile.bind(this);
    this.searchTemplate = this.searchTemplate.bind(this);
    this.handleChangeCompletedReportAction = this.handleChangeCompletedReportAction.bind(this);
  }

  componentDidMount() {
    this.searchTemplate();
    this.handleSearchJobProfile();
  }

  async searchTemplate(query) {
    const { currentAccount, growl } = this.props;
    try {
      this.setState({
        isTempLoading: true,
        temps: [],
      });
      const data = await searchAsmtTemp({
        aid: currentAccount,
        query,
      });
      this.setState({
        temps: data,
        isTempLoading: false,
      });
    } catch (err) {
      growlErrors(growl, err);
      this.setState({
        isTempLoading: false,
      });
    }
  }

  handleSelectTemplate(selectedTemp) {
    if (selectedTemp) {
      const { id } = selectedTemp;
      this.setState({ asmtTemp: id });
      this.fetchJobProfiles(null, id);
    }
  }

  handleSearchJobProfile(query) {
    const { asmtTemp } = this.state;
    this.fetchJobProfiles(query, asmtTemp);
  }

  async fetchJobProfiles(query = null, asmtTemp = null) {
    const { growl, currentAccount } = this.props;
    try {
      this.setState({ jobSearching: true });
      const data = await searchJobProfile({
        aid: currentAccount,
        query,
        tempId: asmtTemp,
      });
      this.setState({ jobs: data, jobSearching: false });
    } catch (err) {
      this.setState({ jobSearching: false });
      growlErrors(growl, err);
    }
  }

  handleChangeCompletedReportAction() {
    const { completedReportAction } = this.state;
    this.setState({ completedReportAction: !completedReportAction });
  }

  render() {
    const {
      onCancel,
      activeLocales,
      onSave,
      intl: { formatMessage },
      locale,
    } = this.props;
    const {
      jobs, jobSearching, temps, isTempLoading, completedReportAction,
    } = this.state;
    const initialValues = {
      locale,
    };
    const validationSchema = buildValidationSchema(formatMessage);
    return (
      <Panel header={<FormattedMessage id="assessment.new" defaultMessage="New Assessment" />}>
        <Formik
          validateOnBlur={false}
          initialValues={initialValues}
          validationSchema={validationSchema}
          onSubmit={async (values, formikBag) => {
            const { resetForm, setSubmitting } = formikBag;
            await onSave(values, resetForm);
            setSubmitting(false);
          }}
          render={formProps => (
            <NewAssessmentForm
              formProps={formProps}
              jobs={jobs}
              jobSearching={jobSearching}
              searchJob={this.handleSearchJobProfile}
              completedReportAction={completedReportAction}
              handleCancel={onCancel}
              activeLocales={activeLocales}
              assessmentTemplates={temps}
              isTempLoading={isTempLoading}
              searchTemp={this.searchTemplate}
              handleSelectTemplate={this.handleSelectTemplate}
              changeCompletedReportAction={this.handleChangeCompletedReportAction}
            />
          )}
        />
      </Panel>
    );
  }
}

NewAssessment.propTypes = {
  currentAccount: PropTypes.string.isRequired,
  intl: PropTypes.shape({
    formatMessage: PropTypes.func.isRequired,
    formatDate: PropTypes.func.isRequired,
  }).isRequired,
  onCancel: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
  activeLocales: PropTypes.arrayOf(PropTypes.any).isRequired,
  growl: growlShape.isRequired,
  locale: PropTypes.string.isRequired,
};

const mapState = state => ({
  locale: state.i18n.locale,
});

export default connect(mapState)(withGrowl(injectIntl(withAccount(NewAssessment))));
